import { inject, Injectable } from '@angular/core';
import { FUSE_CONFIG } from '@fuse/services/config/config.constants';
import { FuseConfig, Scheme } from '@fuse/services/config/config.types';
import { merge } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class FuseConfigService {
    private readonly _schemeStorageKey = 'fuse-scheme';
    private readonly _defaultConfig = inject(FUSE_CONFIG) as FuseConfig;
    private _config = new BehaviorSubject(
        this._mergeStoredScheme(this._defaultConfig)
    );

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for config
     */
    set config(value: any) {
        // Merge the new config over to the current config
        const config = merge({}, this._config.getValue(), value);
        this._persistScheme(config.scheme);

        // Execute the observable
        this._config.next(config);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    get config$(): Observable<any> {
        return this._config.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Resets the config to the default
     */
    reset(): void {
        // Set the config
        this._config.next(this.config);
    }

    private _mergeStoredScheme(config: FuseConfig): FuseConfig {
        const scheme = this._getStoredScheme();
        return merge({}, config, scheme ? { scheme } : {});
    }

    private _getStoredScheme(): Scheme | null {
        try {
            const scheme = globalThis.localStorage?.getItem(
                this._schemeStorageKey
            );
            return this._isValidScheme(scheme) ? scheme : null;
        } catch {
            return null;
        }
    }

    private _persistScheme(scheme: Scheme | undefined): void {
        if (!this._isValidScheme(scheme)) {
            return;
        }

        try {
            globalThis.localStorage?.setItem(this._schemeStorageKey, scheme);
        } catch {
            return;
        }
    }

    private _isValidScheme(scheme: unknown): scheme is Scheme {
        return scheme === 'auto' || scheme === 'dark' || scheme === 'light';
    }
}
